// ============================================================================
// HOLIDAY FORM MODAL
// ============================================================================
// modal-md — Standard single-record edit modal for holidays.
// Follows the prima-web mandatory form-modal blueprint (see CLAUDE.md).
//
// Edit-only — create uses the HolidayBulkAddModal.
//
// Fields: Year (disabled), Location (disabled), Date, Day (computed),
//         Description, Type, Status, Optional checkbox.

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface HolidayFormData {
  year: number;
  officeId: string;
  officeName: string;
  holidayDate: string;
  description: string;
  holidayType: string;
  isOptional: boolean;
  statusCode: 'ACTIVE' | 'INACTIVE';
}

export interface HolidayFormModalProps {
  isOpen: boolean;
  mode: 'edit';
  initialData?: HolidayFormData | null;
  isSaving?: boolean;
  apiError?: string | null;
  onSave: (data: HolidayFormData) => void;
  onClose: () => void;
  // Minimize support
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'edit', instanceId?: string, recordId?: string) => void;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const INITIAL_FORM_DATA: HolidayFormData = {
  year: new Date().getFullYear(),
  officeId: '',
  officeName: '',
  holidayDate: '',
  description: '',
  holidayType: '',
  isOptional: false,
  statusCode: 'ACTIVE',
};

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const HOLIDAY_TYPE_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'NATIONAL', label: 'National' },
  { value: 'REGIONAL', label: 'Regional' },
  { value: 'RELIGIOUS', label: 'Religious' },
  { value: 'COMPANY', label: 'Company' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const HolidayFormModal: React.FC<HolidayFormModalProps> = ({
  isOpen,
  mode,
  initialData,
  isSaving = false,
  apiError,
  onSave,
  onClose,
  tabId,
  moduleName,
  onRestoreModal,
  minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  // --- Internal form state (Pattern B) ---
  const [formData, setFormData] = useState<HolidayFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const initialDataRef = useRef(initialData);
  initialDataRef.current = initialData;
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);

  // --- Validation state ---
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback(
    (field: string) => fieldErrors[field],
    [fieldErrors],
  );
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // --- Confirm dialog state ---
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  // --- Populate / reset form on open ---
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const src = initialDataRef.current;
    const data: HolidayFormData = src
      ? {
          year: src.year,
          officeId: src.officeId ?? '',
          officeName: src.officeName ?? '',
          holidayDate: src.holidayDate ?? '',
          description: src.description ?? '',
          holidayType: src.holidayType ?? '',
          isOptional: src.isOptional ?? false,
          statusCode: (src.statusCode as 'ACTIVE' | 'INACTIVE') ?? 'ACTIVE',
        }
      : { ...INITIAL_FORM_DATA };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  // --- Dirty tracking ---
  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  // --- Computed day name ---
  const dayName = useMemo(() => {
    if (!formData.holidayDate) return '-';
    try {
      return new Date(formData.holidayDate + 'T00:00:00').toLocaleDateString('en-US', {
        weekday: 'long',
      });
    } catch {
      return '-';
    }
  }, [formData.holidayDate]);

  // --- Close handler ---
  const handleClose = useCallback(() => {
    if (isSaving) return;
    if (isDirty) {
      setDiscardConfirmOpen(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose, isSaving]);

  // --- Minimize handler ---
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const modalId = `${tabId}-form-modal-edit-${currentFormData.officeId}-${currentFormData.holidayDate}`;
    const title = `Edit - ${currentFormData.description || 'Holiday'}`;

    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot
      ? JSON.stringify(currentFormData) !== savedSnapshot
      : false;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) {
          onRestoreModal('edit', undefined, currentFormData.officeId);
        }
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  // --- Validate then show save confirmation ---
  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};

    if (!formData.holidayDate) {
      errors.holidayDate = 'Date is required';
    } else {
      const rowYear = parseInt(formData.holidayDate.substring(0, 4), 10);
      if (rowYear !== formData.year) {
        errors.holidayDate = `Date must be in year ${formData.year}`;
      }
    }

    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    } else if (formData.description.length > 200) {
      errors.description = 'Description must be 200 characters or less';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const firstField = Object.keys(errors)[0];
        const el = document.querySelector<HTMLElement>(`[data-field="${firstField}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLInputElement>('input, textarea')?.focus();
        }
      });
      return;
    }

    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  // --- Confirmed save ---
  const handleConfirmSave = useCallback(() => {
    setSaveConfirmOpen(false);
    onSave({
      ...formData,
      description: formData.description.trim(),
    });
  }, [formData, onSave]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {/* Loading overlay */}
        {isSaving && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'var(--modal-loading-overlay)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              borderRadius: 'inherit',
              gap: '0.75rem',
            }}
          >
            <div
              style={{
                width: '2rem',
                height: '2rem',
                border: '3px solid var(--border-color)',
                borderTopColor: '#2563eb',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              Saving...
            </span>
          </div>
        )}

        {/* Header */}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Edit Holiday</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button
                className="modal-header-btn"
                onClick={handleMinimize}
                title="Minimize to bar"
                disabled={isSaving}
              >
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button
              className="modal-close"
              onClick={handleClose}
              disabled={isSaving}
              title="Close"
            >
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="modal-content">
          {apiError && (
            <div
              style={{
                marginBottom: '0.75rem',
                padding: '0.625rem 0.75rem',
                background: 'rgba(220, 38, 38, 0.08)',
                border: '1px solid #dc2626',
                borderRadius: '4px',
                color: '#dc2626',
                fontSize: '0.8125rem',
                lineHeight: 1.4,
              }}
            >
              {apiError}
            </div>
          )}

          {/* Row 1: Year + Location (read-only) */}
          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Year</label>
              <input
                type="number"
                className="form-input"
                value={formData.year}
                disabled
              />
            </div>
            <div className="form-group">
              <label className="form-label">Location</label>
              <input
                type="text"
                className="form-input"
                value={formData.officeName}
                disabled
              />
            </div>
          </div>

          {/* Row 2: Date + Day */}
          <div className="form-grid-2">
            <div className="form-group" data-field="holidayDate">
              <label className="form-label">
                Date <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className={`form-input${getFieldError('holidayDate') ? ' form-field-error' : ''}`}
                value={formData.holidayDate}
                disabled={isSaving}
                onChange={(e) => {
                  clearFieldError('holidayDate');
                  setFormData((prev) => ({ ...prev, holidayDate: e.target.value }));
                }}
              />
              {getFieldError('holidayDate') && (
                <div className="form-error-message">{getFieldError('holidayDate')}</div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Day</label>
              <input
                type="text"
                className="form-input"
                value={dayName}
                disabled
              />
            </div>
          </div>

          {/* Row 3: Description */}
          <div className="form-group" data-field="description">
            <label className="form-label">
              Description <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-input${getFieldError('description') ? ' form-field-error' : ''}`}
              value={formData.description}
              maxLength={200}
              placeholder="Holiday name"
              disabled={isSaving}
              onChange={(e) => {
                clearFieldError('description');
                setFormData((prev) => ({ ...prev, description: e.target.value }));
              }}
            />
            {getFieldError('description') && (
              <div className="form-error-message">{getFieldError('description')}</div>
            )}
          </div>

          {/* Row 4: Type + Status */}
          <div className="form-grid-2">
            <div className="form-group" data-field="holidayType">
              <SyutSelect
                label="Type"
                value={formData.holidayType}
                options={HOLIDAY_TYPE_OPTIONS}
                isClearable={false}
                size="sm"
                onChange={(val) => {
                  setFormData((prev) => ({ ...prev, holidayType: (val as string) || '' }));
                }}
              />
            </div>
            <div className="form-group" data-field="statusCode">
              <SyutSelect
                label="Status"
                value={formData.statusCode}
                options={STATUS_OPTIONS}
                isClearable={false}
                size="sm"
                onChange={(val) => {
                  setFormData((prev) => ({
                    ...prev,
                    statusCode: (val as 'ACTIVE' | 'INACTIVE') || 'ACTIVE',
                  }));
                }}
              />
            </div>
          </div>

          {/* Row 5: Optional checkbox */}
          <div className="form-group">
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.875rem',
                color: 'var(--text-primary)',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={formData.isOptional}
                disabled={isSaving}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, isOptional: e.target.checked }))
                }
              />
              Optional Holiday
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
            Save Changes
          </button>
        </div>
      </div>

      {/* Save confirmation */}
      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Update"
        message={`Save changes to "${formData.description.trim()}"?`}
        confirmLabel="Save"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      {/* Discard changes confirmation */}
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => {
          setDiscardConfirmOpen(false);
          onClose();
        }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default HolidayFormModal;
